import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Envelope, EnvelopeOpen, Star, Paperclip, Trash, ArrowsClockwise,
  PaperPlaneTilt, MagnifyingGlass, X, ArrowLeft, Tag, GoogleLogo,
  ArrowBendUpLeft, ArrowsLeftRight, ArrowBendDoubleUpLeft, CheckSquare
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SYS_LABEL_NAMES = {
  INBOX: 'Boite de reception',
  STARRED: 'Suivis',
  IMPORTANT: 'Important',
  SENT: 'Envoyes',
  DRAFT: 'Brouillons',
  SPAM: 'Spam',
  TRASH: 'Corbeille',
  CATEGORY_PERSONAL: 'Personnel',
  CATEGORY_SOCIAL: 'Reseaux sociaux',
  CATEGORY_PROMOTIONS: 'Promotions',
  CATEGORY_UPDATES: 'Mises a jour',
  CATEGORY_FORUMS: 'Forums',
  UNREAD: 'Non lus',
};

const formatDate = (s) => {
  if (!s) return '';
  try {
    const d = new Date(s);
    const today = new Date();
    if (d.toDateString() === today.toDateString()) {
      return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    }
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
  } catch { return s; }
};

const stripName = (from) => {
  if (!from) return '';
  const m = from.match(/^"?([^"<]+?)"?\s*<.+>$/);
  return m ? m[1].trim() : from;
};

const extractEmail = (header) => {
  if (!header) return '';
  const m = header.match(/<([^>]+)>/);
  return (m ? m[1] : header).trim().toLowerCase();
};

const splitEmails = (s) => (s || '').split(/[,;]/).map(x => x.trim()).filter(Boolean);

const cleanReplySubject = (subject) => {
  if (!subject) return 'Re: ';
  return /^re\s*:/i.test(subject) ? subject : `Re: ${subject}`;
};

const cleanForwardSubject = (subject) => {
  if (!subject) return 'Tr: ';
  return /^(tr|fwd?)\s*:/i.test(subject) ? subject : `Tr: ${subject}`;
};

const buildQuotedBody = (msg, mode) => {
  const dateStr = msg.date || '';
  const fromStr = msg.from || '';
  const text = msg.text || msg.snippet || '';
  const quoted = text.split('\n').map(l => `> ${l}`).join('\n');
  if (mode === 'forward') {
    return `\n\n---------- Message transfere ----------\nDe : ${fromStr}\nDate : ${dateStr}\nObjet : ${msg.subject || ''}\nA : ${msg.to || ''}\n${msg.cc ? `Cc : ${msg.cc}\n` : ''}\n${text}\n`;
  }
  return `\n\nLe ${dateStr}, ${fromStr} a ecrit :\n${quoted}\n`;
};

// --- Contact autocomplete input (allows free-typed emails + picks from contact directory) ---
const ContactInput = ({ value, onChange, contacts, placeholder, required, testId, multiple = true }) => {
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const wrapRef = useRef(null);

  const lastFragment = useMemo(() => {
    if (!multiple) return value || '';
    const parts = (value || '').split(',');
    return (parts[parts.length - 1] || '').trim();
  }, [value, multiple]);

  const suggestions = useMemo(() => {
    if (!contacts || contacts.length === 0) return [];
    const frag = lastFragment.toLowerCase();
    if (!frag) return [];
    const seenEmails = new Set();
    const out = [];
    for (const c of contacts) {
      if (!c.email) continue;
      const email = c.email.toLowerCase();
      if (seenEmails.has(email)) continue;
      const label = `${c.name || c.company || ''}`.toLowerCase();
      const company = (c.company || '').toLowerCase();
      if (email.includes(frag) || label.includes(frag) || company.includes(frag)) {
        seenEmails.add(email);
        out.push(c);
        if (out.length >= 8) break;
      }
    }
    return out;
  }, [contacts, lastFragment]);

  useEffect(() => {
    const handle = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  const pick = (contact) => {
    const email = contact.email;
    if (!multiple) {
      onChange(email);
    } else {
      const parts = (value || '').split(',');
      parts[parts.length - 1] = ` ${email}`;
      onChange(parts.join(',').replace(/^[\s,]+/, '') + ', ');
    }
    setOpen(false);
    setActiveIdx(-1);
  };

  const handleKey = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, suggestions.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && activeIdx >= 0) { e.preventDefault(); pick(suggestions[activeIdx]); }
    else if (e.key === 'Escape') setOpen(false);
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <input
        type="text"
        required={required}
        className="form-input"
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); setActiveIdx(-1); }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKey}
        placeholder={placeholder}
        data-testid={testId}
        autoComplete="off"
      />
      {open && suggestions.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1100,
          backgroundColor: 'white', border: '1px solid #E5E7EB', borderRadius: 8,
          boxShadow: '0 6px 24px rgba(0,0,0,0.12)', marginTop: 4, maxHeight: 260, overflowY: 'auto'
        }}>
          {suggestions.map((c, i) => (
            <div
              key={c.id}
              onMouseDown={(e) => { e.preventDefault(); pick(c); }}
              onMouseEnter={() => setActiveIdx(i)}
              data-testid={`contact-suggest-${i}`}
              style={{
                padding: '8px 12px', cursor: 'pointer', fontSize: '0.85rem',
                backgroundColor: i === activeIdx ? '#F3F4F6' : 'white',
                borderBottom: i === suggestions.length - 1 ? 'none' : '1px solid #F3F4F6'
              }}
            >
              <div style={{ fontWeight: 600, color: '#1E2030' }}>{c.name || c.company || c.email}</div>
              <div style={{ fontSize: '0.78rem', color: '#5E5F66' }}>
                {c.email}{c.company && c.name ? ` - ${c.company}` : ''}
                {c.category ? <span style={{ marginLeft: 6, fontSize: '0.68rem', padding: '1px 6px', borderRadius: 999, backgroundColor: '#EFF6FF', color: '#1D4ED8', textTransform: 'capitalize' }}>{c.category}</span> : null}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const GmailPage = () => {
  const [labels, setLabels] = useState([]);
  const [currentLabel, setCurrentLabel] = useState('INBOX');
  const [messages, setMessages] = useState([]);
  const [selectedMsgId, setSelectedMsgId] = useState(null);
  const [selectedMsg, setSelectedMsg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [profile, setProfile] = useState(null);
  const [showCompose, setShowCompose] = useState(false);
  const [composeForm, setComposeForm] = useState({ to: '', subject: '', body: '', cc: '', reply_to_msg_id: '' });
  const [composeFiles, setComposeFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [oauthError, setOauthError] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [showToTask, setShowToTask] = useState(false);
  const [taskForm, setTaskForm] = useState({ title: '', description: '', date: '', folder_id: '', priority: 'normal' });
  const [taskFolders, setTaskFolders] = useState([]);
  const [savingTask, setSavingTask] = useState(false);

  const fetchContacts = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/contacts`, { withCredentials: true });
      setContacts((r.data || []).filter(c => c.email));
    } catch { /* silent */ }
  }, []);

  const fetchTaskFolders = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/planning/folders?folder_type=task`, { withCredentials: true });
      // Filter out the virtual trash folder
      setTaskFolders((r.data || []).filter(f => !f.is_trash && f.id !== 'trash'));
    } catch { /* silent */ }
  }, []);

  const openConvertToTask = (msg) => {
    const sender = msg.from || '';
    const dateStr = msg.date || new Date().toLocaleString('fr-FR');
    const subj = msg.subject || '(sans objet)';
    const extract = (msg.text || msg.snippet || '').slice(0, 400);
    const description = `Email recu de : ${sender}\nLe : ${dateStr}\n\n${extract}${extract.length >= 400 ? '...' : ''}\n\n[Email Gmail ID: ${msg.id}]`;
    const today = new Date().toISOString().split('T')[0];
    setTaskForm({
      title: subj.slice(0, 200),
      description,
      date: today,
      folder_id: '',
      priority: 'normal',
    });
    fetchTaskFolders();
    setShowToTask(true);
  };

  const submitConvertToTask = async (e) => {
    e.preventDefault();
    if (!taskForm.title.trim()) { toast.error('Titre requis'); return; }
    setSavingTask(true);
    try {
      await axios.post(`${API_URL}/api/planning/tasks-reminders`, {
        title: taskForm.title.trim(),
        description: taskForm.description,
        date: taskForm.date || new Date().toISOString().split('T')[0],
        time: '09:00',
        priority: taskForm.priority,
        folder_id: taskForm.folder_id || null,
        reminder: true,
      }, { withCredentials: true });
      toast.success('Tache creee', {
        description: 'Retrouvez-la dans Notes & Taches',
        action: { label: 'Ouvrir', onClick: () => { window.location.href = '/notes-taches'; } },
      });
      setShowToTask(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur creation tache');
    } finally {
      setSavingTask(false);
    }
  };

  const openCompose = (preset = {}) => {
    setComposeForm({
      to: preset.to || '',
      cc: preset.cc || '',
      subject: preset.subject || '',
      body: preset.body || '',
      reply_to_msg_id: preset.reply_to_msg_id || '',
    });
    setComposeFiles([]);
    setShowCompose(true);
  };

  const replyToMsg = (msg, all = false) => {
    const senderEmail = extractEmail(msg.from);
    let to = senderEmail;
    let cc = '';
    if (all) {
      const myEmail = (profile?.email || '').toLowerCase();
      const others = [...splitEmails(msg.to), ...splitEmails(msg.cc)]
        .map(extractEmail)
        .filter(e => e && e !== myEmail && e !== senderEmail);
      cc = Array.from(new Set(others)).join(', ');
    }
    openCompose({
      to,
      cc,
      subject: cleanReplySubject(msg.subject),
      body: buildQuotedBody(msg, 'reply'),
      reply_to_msg_id: msg.id,
    });
  };

  const forwardMsg = (msg) => {
    openCompose({
      to: '',
      subject: cleanForwardSubject(msg.subject),
      body: buildQuotedBody(msg, 'forward'),
      reply_to_msg_id: '',
    });
  };

  const fetchProfile = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/gmail/profile`, { withCredentials: true });
      setProfile(r.data);
      setOauthError(null);
    } catch (e) {
      setOauthError(e.response?.data?.detail || 'Google non connecte');
    }
  }, []);

  const fetchLabels = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/gmail/labels`, { withCredentials: true });
      setLabels(r.data);
    } catch (e) {
      // already handled by oauthError state
    }
  }, []);

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('max_results', '40');
      if (search) {
        params.set('q', search);
      } else if (currentLabel) {
        params.set('label', currentLabel);
      }
      const r = await axios.get(`${API_URL}/api/gmail/messages?${params}`, { withCredentials: true });
      setMessages(r.data.messages || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur chargement messages');
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }, [currentLabel, search]);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);
  useEffect(() => { if (profile) { fetchLabels(); fetchContacts(); } }, [profile, fetchLabels, fetchContacts]);
  useEffect(() => { if (profile) { fetchMessages(); setSelectedMsgId(null); setSelectedMsg(null); } }, [profile, currentLabel, fetchMessages]);

  const openMessage = async (id) => {
    setSelectedMsgId(id);
    setSelectedMsg(null);
    try {
      const r = await axios.get(`${API_URL}/api/gmail/messages/${id}`, { withCredentials: true });
      setSelectedMsg(r.data);
      if ((r.data.label_ids || []).includes('UNREAD')) {
        try {
          await axios.post(`${API_URL}/api/gmail/messages/${id}/mark-read`, {}, { withCredentials: true });
          setMessages(prev => prev.map(m => m.id === id ? { ...m, unread: false } : m));
          fetchLabels();
        } catch { /* ignore */ }
      }
    } catch (e) {
      toast.error('Erreur ouverture message');
    }
  };

  const trashMessage = async (id) => {
    if (!window.confirm('Deplacer ce message dans la corbeille Gmail ?')) return;
    try {
      await axios.post(`${API_URL}/api/gmail/messages/${id}/trash`, {}, { withCredentials: true });
      toast.success('Deplace dans la corbeille');
      setSelectedMsgId(null);
      setSelectedMsg(null);
      fetchMessages();
      fetchLabels();
    } catch { toast.error('Erreur'); }
  };

  const toggleStar = async (id, currentStar) => {
    try {
      await axios.post(`${API_URL}/api/gmail/messages/${id}/star?star=${!currentStar}`, {}, { withCredentials: true });
      setMessages(prev => prev.map(m => m.id === id ? { ...m, starred: !currentStar } : m));
      if (selectedMsg && selectedMsg.id === id) {
        setSelectedMsg({ ...selectedMsg, starred: !currentStar });
      }
    } catch { toast.error('Erreur'); }
  };

  const handleCompose = async (e) => {
    e.preventDefault();
    if (!composeForm.to.trim()) { toast.error('Destinataire requis'); return; }
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('to', composeForm.to);
      fd.append('subject', composeForm.subject);
      fd.append('body', composeForm.body);
      if (composeForm.cc) fd.append('cc', composeForm.cc);
      if (composeForm.reply_to_msg_id) fd.append('reply_to_msg_id', composeForm.reply_to_msg_id);
      composeFiles.forEach(f => fd.append('files', f));
      await axios.post(`${API_URL}/api/gmail/send`, fd, { withCredentials: true });
      toast.success('Email envoye');
      setShowCompose(false);
      setComposeForm({ to: '', subject: '', body: '', cc: '', reply_to_msg_id: '' });
      setComposeFiles([]);
      if (currentLabel === 'SENT') fetchMessages();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur envoi');
    } finally {
      setSending(false);
    }
  };

  if (oauthError) {
    return (
      <div className="page-content">
        <div className="page-header">
          <h1 className="page-title">Gmail</h1>
          <p className="page-subtitle">Consultez et envoyez vos emails directement depuis BATI-BOYS</p>
        </div>
        <div className="card" style={{ padding: '40px', textAlign: 'center', maxWidth: '600px', margin: '0 auto' }}>
          <GoogleLogo size={56} weight="bold" color="#34A853" style={{ marginBottom: '16px' }} />
          <h2 style={{ fontSize: '1.2rem', marginBottom: '8px' }}>Connexion Google requise</h2>
          <p style={{ color: '#5E5F66', marginBottom: '24px' }}>{oauthError}</p>
          <a className="btn btn-primary" href="/planning" data-testid="goto-planning-connect">
            Aller dans Planning pour connecter Google
          </a>
          <p style={{ fontSize: '0.78rem', color: '#9E9EA7', marginTop: '20px' }}>
            Si vous etiez deja connecte a Google Calendar, vous devrez vous deconnecter puis vous reconnecter pour autoriser l'acces a Gmail.
          </p>
        </div>
      </div>
    );
  }

  const totalUnread = labels.find(l => l.id === 'INBOX')?.messages_unread || 0;

  return (
    <div className="page-content" style={{ height: 'calc(100vh - 140px)', display: 'flex', flexDirection: 'column' }}>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <GoogleLogo size={28} weight="bold" color="#EA4335" /> Gmail
            {totalUnread > 0 && <span style={{ marginLeft: 8, fontSize: '0.85rem', backgroundColor: '#EA4335', color: 'white', padding: '2px 10px', borderRadius: 999 }}>{totalUnread} non lu{totalUnread > 1 ? 's' : ''}</span>}
          </h1>
          <p className="page-subtitle">{profile?.email || ''}</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary" onClick={() => { fetchMessages(); fetchLabels(); }} title="Actualiser" data-testid="refresh-gmail-btn">
            <ArrowsClockwise size={16} />
          </button>
          <button className="btn btn-primary" onClick={() => openCompose()} data-testid="compose-email-btn">
            <PaperPlaneTilt size={16} weight="bold" /> Nouveau message
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, flex: 1, minHeight: 0 }}>
        {/* Sidebar : labels */}
        <div className="card" style={{ padding: 10, minWidth: 220, maxWidth: 250, overflowY: 'auto' }}>
          <div style={{ marginBottom: 10, position: 'relative' }}>
            <MagnifyingGlass size={14} style={{ position: 'absolute', left: 8, top: 9, color: '#9CA3AF' }} />
            <input
              type="text"
              className="form-input"
              placeholder="Rechercher..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') fetchMessages(); }}
              data-testid="gmail-search-input"
              style={{ paddingLeft: 28, fontSize: '0.85rem', padding: '6px 8px 6px 28px' }}
            />
            {search && <X size={14} onClick={() => { setSearch(''); fetchMessages(); }} style={{ position: 'absolute', right: 8, top: 9, color: '#9CA3AF', cursor: 'pointer' }} />}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {labels.map(l => {
              const active = currentLabel === l.id;
              const display = SYS_LABEL_NAMES[l.id] || l.name;
              return (
                <div
                  key={l.id}
                  onClick={() => { setSearch(''); setCurrentLabel(l.id); }}
                  data-testid={`gmail-label-${l.id}`}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px',
                    borderRadius: 6, cursor: 'pointer',
                    backgroundColor: active ? '#FCE8E6' : 'transparent',
                    color: active ? '#B91C1C' : '#1E2030',
                    fontWeight: l.messages_unread > 0 ? 700 : 500,
                    fontSize: '0.85rem',
                  }}
                  onMouseEnter={(e) => { if (!active) e.currentTarget.style.backgroundColor = '#F4F5F7'; }}
                  onMouseLeave={(e) => { if (!active) e.currentTarget.style.backgroundColor = 'transparent'; }}
                >
                  <Tag size={14} weight={active ? 'fill' : 'regular'} />
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{display}</span>
                  {l.messages_unread > 0 && <span style={{ fontSize: '0.7rem', color: '#B91C1C', fontWeight: 700 }}>{l.messages_unread}</span>}
                </div>
              );
            })}
          </div>
        </div>

        {/* Message list */}
        <div className="card" style={{ flex: selectedMsg ? '0 0 360px' : 1, padding: 0, overflowY: 'auto', minWidth: 0 }}>
          {loading ? (
            <div style={{ padding: 30, textAlign: 'center', color: '#5E5F66' }}>Chargement...</div>
          ) : messages.length === 0 ? (
            <div style={{ padding: 30, textAlign: 'center', color: '#9E9EA7' }}>
              <Envelope size={36} style={{ opacity: 0.4 }} />
              <p style={{ marginTop: 8 }}>Aucun message.</p>
            </div>
          ) : (
            <div>
              {messages.map(m => (
                <div
                  key={m.id}
                  onClick={() => openMessage(m.id)}
                  data-testid={`gmail-msg-${m.id}`}
                  style={{
                    display: 'flex', flexDirection: 'column', gap: 2,
                    padding: '10px 14px',
                    borderBottom: '1px solid #E5E7EB',
                    cursor: 'pointer',
                    backgroundColor: selectedMsgId === m.id ? '#FEF3C7' : (m.unread ? 'white' : '#FAFAFA'),
                  }}
                  onMouseEnter={(e) => { if (selectedMsgId !== m.id) e.currentTarget.style.backgroundColor = '#F4F5F7'; }}
                  onMouseLeave={(e) => { if (selectedMsgId !== m.id) e.currentTarget.style.backgroundColor = m.unread ? 'white' : '#FAFAFA'; }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <span style={{ fontWeight: m.unread ? 700 : 500, fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                      {stripName(m.from)}
                    </span>
                    <span style={{ fontSize: '0.7rem', color: '#5E5F66', flexShrink: 0 }}>{formatDate(m.date)}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Star
                      size={14}
                      weight={m.starred ? 'fill' : 'regular'}
                      color={m.starred ? '#F59E0B' : '#9CA3AF'}
                      style={{ cursor: 'pointer', flexShrink: 0 }}
                      onClick={(e) => { e.stopPropagation(); toggleStar(m.id, m.starred); }}
                    />
                    <span style={{ fontWeight: m.unread ? 700 : 400, fontSize: '0.82rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {m.subject || '(sans objet)'}
                    </span>
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#5E5F66', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {m.snippet}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Reading pane */}
        {selectedMsg && (
          <div className="card" style={{ flex: 1, padding: 20, overflowY: 'auto', minWidth: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
              <button className="btn btn-secondary btn-sm" onClick={() => { setSelectedMsg(null); setSelectedMsgId(null); }} title="Fermer">
                <ArrowLeft size={14} /> Retour
              </button>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <button className="btn btn-primary btn-sm" onClick={() => replyToMsg(selectedMsg, false)} title="Repondre" data-testid="reply-btn">
                  <ArrowBendUpLeft size={14} weight="bold" /> Repondre
                </button>
                <button className="btn btn-secondary btn-sm" onClick={() => replyToMsg(selectedMsg, true)} title="Repondre a tous" data-testid="reply-all-btn">
                  <ArrowBendDoubleUpLeft size={14} weight="bold" /> Tous
                </button>
                <button className="btn btn-secondary btn-sm" onClick={() => forwardMsg(selectedMsg)} title="Transferer" data-testid="forward-btn">
                  <ArrowsLeftRight size={14} weight="bold" /> Transferer
                </button>
                <button className="btn btn-secondary btn-sm" onClick={() => openConvertToTask(selectedMsg)} title="Convertir en tache" data-testid="email-to-task-btn" style={{ color: '#1565C0' }}>
                  <CheckSquare size={14} weight="bold" /> En tache
                </button>
                <button className="btn btn-secondary btn-sm" onClick={() => toggleStar(selectedMsg.id, selectedMsg.starred)} title={selectedMsg.starred ? 'Retirer le suivi' : 'Suivre'}>
                  <Star size={14} weight={selectedMsg.starred ? 'fill' : 'regular'} color={selectedMsg.starred ? '#F59E0B' : '#5E5F66'} />
                </button>
                <button className="btn btn-secondary btn-sm" onClick={() => trashMessage(selectedMsg.id)} title="Supprimer" style={{ color: '#C62828' }}>
                  <Trash size={14} />
                </button>
              </div>
            </div>
            <h2 style={{ fontSize: '1.15rem', margin: '0 0 8px 0' }}>{selectedMsg.subject || '(sans objet)'}</h2>
            <div style={{ fontSize: '0.82rem', color: '#5E5F66', marginBottom: 4 }}>De : <strong>{selectedMsg.from}</strong></div>
            <div style={{ fontSize: '0.82rem', color: '#5E5F66', marginBottom: 4 }}>A : {selectedMsg.to}</div>
            {selectedMsg.cc && <div style={{ fontSize: '0.82rem', color: '#5E5F66', marginBottom: 4 }}>Cc : {selectedMsg.cc}</div>}
            <div style={{ fontSize: '0.78rem', color: '#9CA3AF', marginBottom: 16 }}>{selectedMsg.date}</div>
            {selectedMsg.attachments?.length > 0 && (
              <div style={{ marginBottom: 16, padding: 10, backgroundColor: '#F9FAFB', borderRadius: 8, border: '1px solid #E5E7EB' }}>
                <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#5E5F66', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Paperclip size={14} /> {selectedMsg.attachments.length} piece(s) jointe(s)
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {selectedMsg.attachments.map((a, idx) => (
                    <a
                      key={idx}
                      href={`${API_URL}/api/gmail/messages/${selectedMsg.id}/attachments/${a.attachment_id}`}
                      target="_blank" rel="noopener noreferrer"
                      style={{ padding: '4px 10px', borderRadius: 999, backgroundColor: '#E0F2FE', color: '#0C4A6E', fontSize: '0.75rem', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                    >
                      <Paperclip size={12} /> {a.filename}
                    </a>
                  ))}
                </div>
              </div>
            )}
            <div style={{ borderTop: '1px solid #E5E7EB', paddingTop: 12 }}>
              {selectedMsg.html ? (
                <div style={{ fontSize: '0.9rem', lineHeight: 1.5 }} dangerouslySetInnerHTML={{ __html: selectedMsg.html }} />
              ) : (
                <pre style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word', fontFamily: 'inherit', fontSize: '0.9rem', lineHeight: 1.5, color: '#1E2030', margin: 0 }}>
                  {selectedMsg.text || selectedMsg.snippet}
                </pre>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Compose modal */}
      {showCompose && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && !sending && setShowCompose(false)} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="card" style={{ width: '90%', maxWidth: 640, maxHeight: '92vh', display: 'flex', flexDirection: 'column' }}>
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8, margin: 0 }}>
                {composeForm.reply_to_msg_id ? <ArrowBendUpLeft size={18} weight="bold" /> : <PaperPlaneTilt size={18} />}
                {composeForm.reply_to_msg_id ? ' Repondre' : (/^Tr\s*:/i.test(composeForm.subject) ? ' Transferer' : ' Nouveau message')}
              </h3>
              <button onClick={() => setShowCompose(false)} disabled={sending} className="btn btn-secondary btn-sm"><X size={14} /></button>
            </div>
            <form onSubmit={handleCompose} style={{ padding: 20, overflow: 'auto', flex: 1 }}>
              <div className="form-group">
                <label className="form-label">Destinataire</label>
                <ContactInput
                  value={composeForm.to}
                  onChange={(v) => setComposeForm(prev => ({ ...prev, to: v }))}
                  contacts={contacts}
                  placeholder="Tapez un nom ou un email (ex: client@...)"
                  required
                  testId="compose-to"
                />
                {contacts.length > 0 && (
                  <div style={{ fontSize: '0.7rem', color: '#9CA3AF', marginTop: 4 }}>
                    {contacts.length} contact{contacts.length > 1 ? 's' : ''} disponible{contacts.length > 1 ? 's' : ''} dans le repertoire
                  </div>
                )}
              </div>
              <div className="form-group">
                <label className="form-label">Cc (optionnel)</label>
                <ContactInput
                  value={composeForm.cc}
                  onChange={(v) => setComposeForm(prev => ({ ...prev, cc: v }))}
                  contacts={contacts}
                  placeholder="cc1@email.com, cc2@email.com"
                  testId="compose-cc"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Objet</label>
                <input type="text" className="form-input" value={composeForm.subject} onChange={(e) => setComposeForm({ ...composeForm, subject: e.target.value })} data-testid="compose-subject" />
              </div>
              <div className="form-group">
                <label className="form-label">Message</label>
                <textarea className="form-input" rows={10} value={composeForm.body} onChange={(e) => setComposeForm({ ...composeForm, body: e.target.value })} data-testid="compose-body" />
              </div>
              <div className="form-group">
                <label className="form-label">Pieces jointes (optionnel)</label>
                <input type="file" multiple onChange={(e) => setComposeFiles(Array.from(e.target.files || []))} data-testid="compose-files" />
                {composeFiles.length > 0 && (
                  <div style={{ marginTop: 6, fontSize: '0.78rem', color: '#5E5F66' }}>
                    {composeFiles.map((f, i) => <div key={i}><Paperclip size={11} /> {f.name} ({Math.round(f.size / 1024)} Ko) - {f.type || 'type inconnu'}</div>)}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowCompose(false)} disabled={sending}>Annuler</button>
                <button type="submit" className="btn btn-primary" disabled={sending} data-testid="send-email-btn">
                  <PaperPlaneTilt size={14} /> {sending ? 'Envoi...' : 'Envoyer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Convert email to task modal */}
      {showToTask && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && !savingTask && setShowToTask(false)} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="card" style={{ width: '90%', maxWidth: 520, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8, margin: 0 }}>
                <CheckSquare size={18} weight="bold" /> Convertir en tache
              </h3>
              <button onClick={() => setShowToTask(false)} disabled={savingTask} className="btn btn-secondary btn-sm"><X size={14} /></button>
            </div>
            <form onSubmit={submitConvertToTask} style={{ padding: 20, overflow: 'auto', flex: 1 }}>
              <div className="form-group">
                <label className="form-label">Titre de la tache</label>
                <input type="text" required className="form-input" value={taskForm.title} onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })} data-testid="task-title-input" />
              </div>
              <div className="form-group">
                <label className="form-label">Date d'echeance</label>
                <input type="date" className="form-input" value={taskForm.date} onChange={(e) => setTaskForm({ ...taskForm, date: e.target.value })} data-testid="task-date-input" />
              </div>
              <div className="form-group">
                <label className="form-label">Dossier</label>
                <select className="form-input" value={taskForm.folder_id} onChange={(e) => setTaskForm({ ...taskForm, folder_id: e.target.value })} data-testid="task-folder-select">
                  <option value="">Sans dossier</option>
                  {taskFolders.map(f => (
                    <option key={f.id} value={f.id}>{f.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Priorite</label>
                <select className="form-input" value={taskForm.priority} onChange={(e) => setTaskForm({ ...taskForm, priority: e.target.value })} data-testid="task-priority-select">
                  <option value="low">Basse</option>
                  <option value="normal">Normale</option>
                  <option value="high">Haute</option>
                  <option value="urgent">Urgente</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Description (pre-remplie depuis l'email)</label>
                <textarea className="form-input" rows={6} value={taskForm.description} onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })} data-testid="task-description-input" />
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowToTask(false)} disabled={savingTask}>Annuler</button>
                <button type="submit" className="btn btn-primary" disabled={savingTask} data-testid="task-submit-btn">
                  <CheckSquare size={14} /> {savingTask ? 'Creation...' : 'Creer la tache'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GmailPage;
